import React, { useEffect, useRef, useState } from 'react';
import { Zap, Loader, CheckCircle2, AlertTriangle } from 'lucide-react';
import { adminFetch, adminFetchJson } from '../../lib/adminAuth';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CacheWarmerPanel — one-off admin action that pre-warms /api/img cache
 * for the top N most prominent artworks so the first mobile visitor
 * never pays the transcode wait. Polls status until the background job
 * finishes.
 */
const CacheWarmerPanel = () => {
  const [state, setState] = useState({ status: 'idle', total: 0, processed: 0, hits: 0, generated: 0, failed: 0 });
  const [limit, setLimit] = useState(200);
  const [starting, setStarting] = useState(false);
  const pollTimer = useRef(null);

  const load = async () => {
    try {
      const data = await adminFetchJson(`${API_URL}/api/admin/img/warm/status`);
      setState(data || { status: 'idle' });
      return data;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    load();
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, []);

  useEffect(() => {
    if (state.status === 'running') {
      if (!pollTimer.current) {
        pollTimer.current = setInterval(load, 2000);
      }
    } else if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, [state.status]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await adminFetch(`${API_URL}/api/admin/img/warm?limit=${limit}`, { method: 'POST' });
      if (res.status === 409) {
        toast.info('A warm run is already in progress');
      } else if (!res.ok) {
        throw new Error(await res.text());
      } else {
        toast.success('Cache warm started');
      }
      await load();
    } catch (e) {
      toast.error(`Failed to start: ${e.message}`);
    } finally {
      setStarting(false);
    }
  };

  const pct = state.total > 0 ? Math.round((state.processed / state.total) * 100) : 0;
  const isRunning = state.status === 'running';
  const isComplete = state.status === 'complete';

  const statusPill = (() => {
    if (isRunning) return { text: `Running · ${state.processed}/${state.total}`, cls: 'bg-blue-100 text-blue-800', Icon: Loader, spin: true };
    if (isComplete) return { text: `Complete · ${state.generated} variants warmed`, cls: 'bg-green-100 text-green-800', Icon: CheckCircle2 };
    return { text: 'Idle', cls: 'bg-gray-100 text-gray-700', Icon: Zap };
  })();
  const StatusIcon = statusPill.Icon;

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white mb-6" data-testid="cache-warmer-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[3px] text-gray-500 mb-1">
            <Zap className="w-3.5 h-3.5" />
            Image cache warmer
          </div>
          <h2 className="text-2xl font-light">Pre-warm the shop for mobile visitors</h2>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Fires a background job that transcodes AVIF + WebP variants for the top {limit} most prominent artworks
            (featured first, then newest) at both tile (480px) and detail (1200px) widths. First mobile visitor
            skips the ~500 ms transcode wait per image.
          </p>
          <div className="mt-3">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded ${statusPill.cls} text-xs`} data-testid="cache-warmer-status">
              <StatusIcon className={`w-3 h-3 ${statusPill.spin ? 'animate-spin' : ''}`} />
              {statusPill.text}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Top</span>
            <input
              type="number"
              min={1}
              max={1000}
              step={10}
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value) || 200)}
              disabled={isRunning || starting}
              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-black"
              data-testid="cache-warmer-limit"
            />
            <span className="text-gray-500">artworks</span>
          </label>
          <button
            type="button"
            onClick={start}
            disabled={isRunning || starting}
            className="px-4 py-2 text-sm bg-black text-white rounded hover:bg-gray-800 flex items-center gap-2 disabled:opacity-50"
            data-testid="cache-warmer-start"
          >
            {starting || isRunning ? <Loader className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {isRunning ? 'Running…' : 'Start warm'}
          </button>
        </div>
      </div>

      {(isRunning || isComplete) && state.total > 0 && (
        <div className="mt-4">
          <div className="h-2 bg-gray-100 rounded overflow-hidden">
            <div
              className="h-full bg-black transition-all"
              style={{ width: `${pct}%` }}
              data-testid="cache-warmer-progress"
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-600">
            <span>Processed <strong>{state.processed}</strong>/{state.total} ({pct}%)</span>
            <span>Cache hits <strong>{state.hits}</strong></span>
            <span>Generated <strong>{state.generated}</strong></span>
            <span className={state.failed ? 'text-amber-700' : ''}>
              {state.failed > 0 && <AlertTriangle className="inline w-3 h-3 mr-1" />}
              Failed <strong>{state.failed}</strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CacheWarmerPanel;
